import React, { useCallback } from 'react';
import {
  Accordion, styled, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { VisualizerDetailsMonthData, VisualizerMonthData } from '../../models';
import { VisualizerSummaryContainer } from './VisualizerSummaryContainer';
import VisualizerDetailsContainer from './VisualizerDetailsContainer';

const AccordionWrapper = styled(Accordion)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? '#F0F0F0' : 'rgba(0, 0, 0, 0)',
  transition: 'margin 0s linear',
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0)' : '#F0F0F0',
  },
  '&:before': {
    display: 'none',
  },
  '&.MuiPaper-root': {
    borderRadius: '0px',
  },
  '&.Mui-expanded': {
    borderTop: '1px solid gray',
    borderBottom: '1px solid gray',
    marginTop: '-1px',
  },
}));

const AccordionSummaryWrapper = styled(AccordionSummary)(({ theme }) => ({
  width: '100%',
  height: '45px',
  padding: '0 16px',
  [theme.breakpoints.up('sm')]: {
    padding: '0 40px',
  },

  '& .MuiAccordionSummary-content': {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    [theme.breakpoints.down('sm')]: {
      gap: '12px',
    },
    margin: '0 16px 0 0',
  },
}));

const AccordionDetailsWrapper = styled(AccordionDetails)(({ theme }) => ({
  backgroundColor: 'white',
  padding: '0',
}));

interface Props {
  month: VisualizerMonthData;
  detailsMonth: VisualizerDetailsMonthData | undefined;
  detailsLoading: boolean;
  maxWidth: number;
}

function VisualizerAccordion({
  month, detailsMonth, detailsLoading, maxWidth,
}: Props) {
  const accordionOnChange = useCallback((event: React.SyntheticEvent, expanded: boolean) => {
    window.dataLayer.push({
      event: 'Info',
      info_text: `${month.month}-${expanded ? 'expanded' : 'collapsed'}`,
    });
  }, [month.month]);

  return (
    <AccordionWrapper className={month.month} elevation={0} disableGutters onChange={accordionOnChange}>
      <AccordionSummaryWrapper expandIcon={<ArrowDropDownIcon />}>
        <VisualizerSummaryContainer month={month} maxWidth={maxWidth} />
      </AccordionSummaryWrapper>
      <AccordionDetailsWrapper data-testid="accordian-details">
        <VisualizerDetailsContainer month={month} detailsMonth={detailsMonth} detailsLoading={detailsLoading} />
      </AccordionDetailsWrapper>
    </AccordionWrapper>
  );
}

export { VisualizerAccordion };
